import { useEffect, useMemo, useState } from "react";
import {
  ageOn, birthstone, dayOfYear, daysUntilBirthday, isBirthdayToday, pickInspiration,
  weekdayBorn, zodiac, type Dob,
} from "./birthday";
import { fetchOnThisDay, type OnThisDayItem } from "./sessionApi";
import "./BirthdayPanel.css";

/**
 * "On your day" panel. Two modes via `celebration`: the once-a-year birthday celebration (confetti
 * + happy birthday) and the on-demand menu view. Computes zodiac, birthstone, weekday, age and an
 * inspiration locally; fetches "on this day in history" + famous births from the worker (best
 * effort). When no DOB is set it invites the reader to add one. House style: no em dashes.
 */
export default function BirthdayPanel({
  dob,
  name,
  celebration,
  onClose,
  onAddBirthday,
}: {
  dob: Dob | null;
  name?: string;
  celebration: boolean;
  onClose: () => void;
  onAddBirthday: () => void;
}) {
  const [history, setHistory] = useState<{ events: OnThisDayItem[]; births: OnThisDayItem[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!dob) return;
    let cancelled = false;
    setLoading(true);
    fetchOnThisDay(dob.m, dob.d).then((res) => {
      if (!cancelled) { setHistory(res); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [dob?.m, dob?.d]);

  const today = useMemo(() => new Date(), []);
  const insights = useMemo(() => {
    if (!dob) return null;
    return {
      z: zodiac(dob.m, dob.d),
      stone: birthstone(dob.m),
      weekday: weekdayBorn(dob),
      age: ageOn(dob, today),
      days: daysUntilBirthday(dob, today),
      isToday: isBirthdayToday(dob, today),
      inspiration: pickInspiration(dob.d + dob.m * 31 + dayOfYear(today)),
    };
  }, [dob, today]);

  return (
    <div
      className="bday-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={celebration ? "Happy birthday" : "On your day"}
      onClick={onClose}
    >
      <div className={`bday-card${celebration ? " celebrate" : ""}`} onClick={(e) => e.stopPropagation()}>
        {celebration ? (
          <div className="bday-confetti" aria-hidden="true">
            {Array.from({ length: 18 }, (_, i) => <span key={i} style={{ "--i": i } as React.CSSProperties} />)}
          </div>
        ) : null}
        <button type="button" className="bday-close" onClick={onClose} aria-label="Close">&times;</button>

        {!dob || !insights ? (
          <div className="bday-empty">
            <div className="bday-spark" aria-hidden="true">&#127874;</div>
            <h2 className="bday-title">Add your birthday</h2>
            <p className="bday-empty-sub">Tell us your date of birth and we will show you what happened on your day, your zodiac and birthstone, and a thought just for you.</p>
            <button type="button" className="bday-cta" onClick={onAddBirthday}>Add your birthday</button>
          </div>
        ) : (
          <>
            <span className="bday-eyebrow">{celebration ? "Happy birthday" : "On your day"}</span>
            <h2 className="bday-title">
              {celebration
                ? `Happy birthday${name ? ", " + name : ""}!`
                : name ? `${name}, this is your day` : "This is your day"}
            </h2>

            <div className="bday-chips">
              <span className="bday-chip"><strong>{insights.z.emoji} {insights.z.sign}</strong><small>{insights.z.trait}</small></span>
              <span className="bday-chip"><strong>{insights.stone}</strong><small>birthstone</small></span>
              {insights.weekday ? <span className="bday-chip"><strong>{insights.weekday}</strong><small>born on a</small></span> : null}
              {insights.age != null ? <span className="bday-chip"><strong>{insights.age}</strong><small>years young</small></span> : null}
              {!insights.isToday ? <span className="bday-chip"><strong>{insights.days}</strong><small>days to your next</small></span> : null}
            </div>

            <section className="bday-section">
              <h3>On this day in history</h3>
              {loading ? (
                <p className="bday-muted">Looking it up&#8230;</p>
              ) : history && history.events.length ? (
                <ul className="bday-list">
                  {history.events.map((e, i) => <li key={i}><strong>{e.year}</strong> {e.text}</li>)}
                </ul>
              ) : (
                <p className="bday-muted">We could not load today's history. Try again later.</p>
              )}
            </section>

            {history && history.births.length ? (
              <section className="bday-section">
                <h3>Born on your day</h3>
                <ul className="bday-list">
                  {history.births.map((b, i) => <li key={i}><strong>{b.year}</strong> {b.text}</li>)}
                </ul>
              </section>
            ) : null}

            <p className="bday-inspiration">&#10024; {insights.inspiration}</p>
            <button type="button" className="bday-cta" onClick={onClose}>{celebration ? "Thank you" : "Close"}</button>
          </>
        )}
      </div>
    </div>
  );
}
